/**
 * OmniLeagues — Bridge External-ID Reconcile (Task 6).
 * On-demand, read-only per-type report of OL SOT vs WP posts keyed by
 * `_ol_external_id`, plus a SEPARATE manual Re-push button that skips
 * twin_conflicts (contaminated rows). See services/bridge_reconcile.py.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { GitCompareArrows, Loader2, AlertTriangle, Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BridgeReconcileCard({ orgId }) {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const text = isDark ? 'text-slate-100' : 'text-slate-900';
  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';

  const run = async () => {
    if (!orgId) return;
    setLoading(true); setError(null); setNote(null);
    try {
      const r = await axios.get(`${API}/api/sync/tenant/${orgId}/bridge-reconcile`, { withCredentials: true });
      setReport(r.data?.data || {});
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Reconcile failed');
    } finally { setLoading(false); }
  };

  const repush = async (type) => {
    setPushing(type); setError(null); setNote(null);
    try {
      const r = await axios.post(`${API}/api/sync/tenant/${orgId}/bridge-repush`, { type }, { withCredentials: true });
      const d = r.data?.data || {};
      setNote(`Re-push ${type}: enqueued ${d.enqueued}, skipped ${d.skipped_twin_conflict} twin-conflict(s).`);
      await run();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Re-push failed');
    } finally { setPushing(null); }
  };

  const cell = 'py-1.5 px-2 text-right';
  const num = (v, warn) => (
    <td className={`${cell} ${v > 0 ? (warn || text) : muted}`} data-testid={`reconcile-cell`}>{v ?? 0}</td>
  );

  return (
    <Card className={card} data-testid="bridge-reconcile-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <GitCompareArrows className="h-4 w-4 text-teal-400" />
              Bridge Reconcile (external-id)
            </CardTitle>
            <p className={`text-xs ${muted} mt-1`}>
              OL SOT ↔ WordPress by <code>_ol_external_id</code>. Read-only. Re-push is manual and
              skips <span className="text-amber-400">twin-conflicts</span> (same game already on WP under a
              different schedule) so it can never clone.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={run} disabled={loading}
                  className="h-8 text-xs" data-testid="reconcile-run-btn">
            {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <GitCompareArrows className="h-3 w-3 mr-1" />}
            Run reconcile
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-red-500/10 border border-red-700/40 text-red-400 text-xs"
               data-testid="reconcile-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{error}
          </div>
        )}
        {note && (
          <div className="p-2 rounded-md bg-emerald-500/10 border border-emerald-700/40 text-emerald-400 text-xs"
               data-testid="reconcile-note">{note}</div>
        )}
        {!report && !loading && (
          <p className={`text-xs ${muted}`}>Click “Run reconcile” to compare OL vs WordPress.</p>
        )}
        {report && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="reconcile-table">
              <thead>
                <tr className={`${muted} border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <th className="py-1.5 px-2 text-left">Type</th>
                  <th className={cell}>Should</th>
                  <th className={cell}>Matched</th>
                  <th className={cell}>Missing</th>
                  <th className={cell}>Twin-conflict</th>
                  <th className={cell}>Dupes</th>
                  <th className={cell}>Orphans</th>
                  <th className="py-1.5 px-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(report).map(([type, r]) => (
                  <tr key={type} className={`border-b ${isDark ? 'border-slate-800' : 'border-slate-100'}`}
                      data-testid={`reconcile-row-${type}`}>
                    <td className={`py-1.5 px-2 text-left font-medium ${text}`}>{type}</td>
                    {num(r.ol_should)}
                    {num(r.matched, isDark ? 'text-emerald-400' : 'text-emerald-600')}
                    {num(r.missing_count, 'text-amber-400')}
                    {num(r.twin_conflict_count, 'text-orange-400')}
                    {num(r.dupe_count, 'text-red-400')}
                    {num(r.orphans_no_ext)}
                    <td className="py-1.5 px-2 text-right">
                      <Button size="sm" variant="outline"
                              disabled={pushing === type || (r.missing_count || 0) === 0}
                              onClick={() => repush(type)}
                              className="h-7 text-[11px]"
                              data-testid={`reconcile-repush-${type}`}>
                        {pushing === type ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                        Re-push
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className={`text-[11px] ${muted} mt-2`}>
              Re-push only ships TRUE-missing rows; twin-conflicts are left for review (data fix). Dupes = ext id on &gt;1 WP post.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
